export type CropPoint = { x: number; y: number };
export type CropSize = { width: number; height: number };

export type CropPreviewGeometry = {
  scale: number;
  width: number;
  height: number;
  maxX: number;
  maxY: number;
  left: number;
  top: number;
  safePan: CropPoint;
};

export type SourceCrop = CropSize & { x: number; y: number };

export function clampCropValue(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

export function calculateCropPreview(
  natural: CropSize,
  stage: CropSize,
  zoom: number,
  pan: CropPoint,
): CropPreviewGeometry {
  if (natural.width <= 0 || natural.height <= 0 || stage.width <= 0 || stage.height <= 0) {
    return { scale: 1, width: 0, height: 0, maxX: 0, maxY: 0, left: 0, top: 0, safePan: { x: 0, y: 0 } };
  }
  const coverScale = Math.max(stage.width / natural.width, stage.height / natural.height);
  const scale = coverScale * Math.max(1, zoom);
  const width = natural.width * scale;
  const height = natural.height * scale;
  const maxX = Math.max(0, (width - stage.width) / 2);
  const maxY = Math.max(0, (height - stage.height) / 2);
  const safePan = {
    x: clampCropValue(pan.x, -maxX, maxX),
    y: clampCropValue(pan.y, -maxY, maxY),
  };
  return {
    scale,
    width,
    height,
    maxX,
    maxY,
    left: (stage.width - width) / 2 + safePan.x,
    top: (stage.height - height) / 2 + safePan.y,
    safePan,
  };
}

export function calculateSourceCrop(
  natural: CropSize,
  stage: CropSize,
  geometry: CropPreviewGeometry,
): SourceCrop {
  if (natural.width <= 0 || natural.height <= 0 || stage.width <= 0 || stage.height <= 0 || geometry.scale <= 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }
  const width = Math.min(natural.width, stage.width / geometry.scale);
  const height = Math.min(natural.height, stage.height / geometry.scale);
  const x = clampCropValue(
    (natural.width - width) / 2 - geometry.safePan.x / geometry.scale,
    0,
    Math.max(0, natural.width - width),
  );
  const y = clampCropValue(
    (natural.height - height) / 2 - geometry.safePan.y / geometry.scale,
    0,
    Math.max(0, natural.height - height),
  );
  return { x, y, width, height };
}

export function cropHasTargetResolution(source: CropSize, target: CropSize, tolerance = 0.5): boolean {
  return source.width + tolerance >= target.width && source.height + tolerance >= target.height;
}
